// src/pages/lab/processing/components/ProtocolInstancesTable.tsx
import React, { useState, useEffect } from "react";
import {  Button,  Badge, Space, Typography, Tooltip, message } from "antd";
import {  PauseCircleOutlined, PlayCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

// API
import { fetchProcessingGroups, putGroupOnHold, resumeGroupFromHold, markGroupAsFailed } from "../../../../api/labiq/GroupProcessingApi";

// Types
import { ProcessingGroup, GroupProcessingFilter } from "../../../../types/lab/ProtocolTypes";

// Shared components
import { DataTable, PageToolbar, StatusTag } from "../../../../components/shared";

// Components
import ProtocolInstanceModal from "./components/ProtocolInstanceModal";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;


interface SampleProcessingTabProps {
	onRefresh: () => void;
	loading?: boolean;
}

// This compoennt should only show the processing groups that are flagged as a first protocol group as these are the ones that process samples. 
// all other groups process tests within samples.

const SampleProcessingTab: React.FC<SampleProcessingTabProps> = ({ onRefresh, loading = false }) => {
	const [processingGroups, setProcessingGroups] = useState<ProcessingGroup[]>([]);
	const [selectedGroups, setSelectedGroups] = useState<ProcessingGroup[]>([]);
	const [detailsModalOpen, setDetailsModalOpen] = useState(false);
	const [selectedGroup, setSelectedGroup] = useState<ProcessingGroup | null>(null);
	const [groupsLoading, setGroupsLoading] = useState(false);
	const PAGE_SIZE = loadUserPreferences().tableRowsPerPage;

	const groupFilters: GroupProcessingFilter = {
		per_page: 25,
		page: 1,
		sort_by: "created_at",
		sort_order: "desc",
	};

	useEffect(() => {
		loadProcessingGroups();
	}, []);

	const loadProcessingGroups = async () => {
		setGroupsLoading(true);
		try {
			const response = await fetchProcessingGroups(groupFilters);
			let filteredGroups = response.data;
			// Only show first protocol groups
			filteredGroups = filteredGroups.filter(group => group.protocol.is_first_protocol);
			setProcessingGroups(filteredGroups);
		} catch (error) {
			console.error("Failed to load processing groups:", error);
		} finally {
			setGroupsLoading(false);
		}
	};

	const handleViewGroupDetails = (group: ProcessingGroup) => {
		setSelectedGroup(group);
		setDetailsModalOpen(true);
	};

	const handlePutGroupOnHold = async (group: ProcessingGroup, reason?: string) => {
		try {
			await putGroupOnHold(group.id, { reason });
			message.success(`Processing group "${group.group_name}" put on hold`);
			loadProcessingGroups();
		} catch (error) {
			message.error("Failed to put group on hold");
		}
	};

	const handleResumeGroupFromHold = async (group: ProcessingGroup) => {
		try {
			await resumeGroupFromHold(group.id);
			message.success(`Processing group "${group.group_name}" resumed`);
			loadProcessingGroups();
		} catch (error) {
			message.error("Failed to resume group");
		}
	};

	const handleMarkGroupAsFailed = async (group: ProcessingGroup, reason?: string) => {
		try {
			await markGroupAsFailed(group.id, { reason });
			message.success(`Processing group "${group.group_name}" marked as failed`);
			loadProcessingGroups();
		} catch (error) {
			message.error("Failed to mark group as failed");
		}
	};

	const handleBulkGroupHold = async () => {
		try {
			const reason = prompt("Enter reason for putting groups on hold:");
			if (!reason) return;

			for (const group of selectedGroups) {
				if (group.status === "active") {
					await putGroupOnHold(group.id, { reason });
				}
			}
			message.success(`${selectedGroups.length} group(s) put on hold`);
			setSelectedGroups([]);
			loadProcessingGroups();
		} catch (error) {
			message.error("Failed to perform bulk group hold action");
		} 
	};

	const groupProcessingColumns: ColumnsType<ProcessingGroup> = [
		{
			title: "Group Name",
			dataIndex: "group_name",
			key: "group_name",
			fixed: "left",
			render: (text: string) => (
				<Text>
					{text}
				</Text>
			),
		},
		{
			title: "Samples",
			dataIndex: "sample_count",
			key: "sample_count",
			align: "center",
			render: (count: number) => <Badge count={count || 0} showZero style={{ backgroundColor: "var(--mg-primary)" }} />,
		},
		{
			title: "Protocol",
			key: "protocol",
			render: (_: any, record: ProcessingGroup) => (
				 record.protocol?.name 
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status: string) => <StatusTag status={status || "pending"} />,
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			render: (date: string) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Actions",
			key: "actions",
			fixed: "right",
			render: (_: any, record: ProcessingGroup) => (
				<>
					<Tooltip title="Put Group on Hold">
						<Button type="text" size="small" icon={<PauseCircleOutlined />} onClick={() => handlePutGroupOnHold(record, "Manual hold")} style={{ color: "var(--mg-warning)" }} disabled={record.status !== "active"} />
					</Tooltip>
					<Tooltip title="Mark Group as Failed">
						<Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={() => handleMarkGroupAsFailed(record, "Manual failure")} danger disabled={record.status !== "active"} />
					</Tooltip>

					<Tooltip title="Resume Group">
						<Button type="text" size="small" icon={<PlayCircleOutlined />} onClick={() => handleResumeGroupFromHold(record)} style={{ color: "var(--mg-success)" }} disabled={record.status !== "on_hold"} />
					</Tooltip>
				</>
			),
		},
	];

	return (
		<>
			<PageToolbar
				extraActions={[
					{
						label: `Pause ${selectedGroups.length} groups`,
						icon: <PauseCircleOutlined />,
						onClick: handleBulkGroupHold,
						disabled: selectedGroups.length === 0,
					},
					{
						label: "Clear Selection",
						icon: <CloseCircleOutlined />,
						onClick: () => setSelectedGroups([]),
						disabled: selectedGroups.length === 0,
					}
				]}
			/>

			<DataTable
				bare
				columns={groupProcessingColumns}
				dataSource={processingGroups}
				rowKey="id"
				loading={groupsLoading}
				selectedRows={selectedGroups}
				setSelectedRows={setSelectedGroups}
				onRowClick={(record) => handleViewGroupDetails(record)}
				scrollX={1000}
				scrollY="calc(100vh - 305px)"
				pagination={{ pageSize: PAGE_SIZE}}
				style={{ marginTop: 12 }}
			/>

			{detailsModalOpen && selectedGroup && (
				<ProtocolInstanceModal
					group={selectedGroup}
					groupType="sample"
					onClose={() => setDetailsModalOpen(false)}
					onRefresh={() => { onRefresh(); loadProcessingGroups(); }}
					isOpenModal={detailsModalOpen}
				/>
			)}
		</>
	);
};

export default SampleProcessingTab;
